/**
 * Couch geometry library. Entries are DATA, not code forks: adding a couch is
 * a new entry here, never a new branch in the placement or writing code.
 *
 * Coordinates are mm. x = 0 is the lateral centre of the image; y = 0 is the
 * predicted couch top and y increases DOWNWARD, away from the patient. The
 * cross-section is extruded along z onto every CT slice.
 *
 * Each loop is an OPEN ring - the first point is NOT repeated at the end; the
 * DICOM writer closes it. Components are listed OUTERMOST FIRST, and none
 * overlaps another as written: a component inside another names it in
 * `holeIn` and is cut out of it.
 *
 * `density.relElectron` and `density.relMass` are written verbatim as
 * ROIPhysicalPropertyValue (3006,00B4) under REL_ELEC_DENSITY and
 * REL_MASS_DENSITY. They currently carry the SAME number: these are effective
 * densities fitted to reproduce measured attenuation for this geometry, not
 * material properties, so converting between them would be a category error.
 * Do NOT add a conversion.
 *
 * The vendor-tabulated couches share one pair of values, taken from the couch
 * structures Varian's Eclipse writes for its own models: the shell at 0.702
 * (Eclipse assigns −300 HU, exported as 0.70227) and the foam interior at
 * 0.001 (−1000 HU, air), so a couch written here computes through the same
 * attenuation a clinic's own Eclipse couch would. The Radixact entry is fitted
 * separately and has its own values.
 */

export interface CouchComponent {
    readonly roiName: string;
    /**
     * The `roiName` of the component this one is cut out of, when it lies
     * inside another: its loops are then ALSO written as holes in that
     * component, so the two tile the cross-section without overlapping. A
     * shell is a ring, not a slab with an interior drawn over it.
     *
     * Overlap is what this rules out. Where two written structures overlap,
     * the density a planning system computes through is whichever it lets
     * win, and that is the receiving system's rule, not ours. Absent for a
     * component that lies inside no other.
     */
    readonly holeIn?: string;
    readonly density: {
        readonly relElectron: number;
        readonly relMass: number;
    };
    readonly loopsXyMm: ReadonlyArray<ReadonlyArray<readonly [number, number]>>;
}

export interface CouchVariant {
    readonly id: string;
    readonly labelId?: string;
    readonly components: readonly CouchComponent[];
}

export interface CouchModel {
    readonly id: string;
    /** Vendor product name, shown as-is in the model select. Deliberately
     *  NOT translated: like each component's `roiName`, it is vendor
     *  data, not UI prose — a Spanish catalog would still list the
     *  product under its own name. */
    readonly name: string;
    readonly variants: readonly CouchVariant[];
}

export const COUCH_MODELS = [
    {
        id: 'varian_exact_igrt',
        name: 'TrueBeam Exact IGRT Couch',
        variants: [
            {
                id: 'thin',
                labelId: 'couch.variant.thin',
                components: [
                    {
                        roiName: 'CouchShell',
                        density: { relElectron: 0.702, relMass: 0.702 },
                        loopsXyMm: [
                            [
                                [-257.41, 0.94],
                                [-252.73, 0.0],
                                [252.73, 0.0],
                                [258.97, 1.59],
                                [263.22, 5.77],
                                [264.99, 12.01],
                                [264.03, 16.69],
                                [261.32, 21.37],
                                [251.62, 32.29],
                                [241.81, 40.5],
                                [234.01, 45.01],
                                [221.53, 49.02],
                                [210.61, 50.0],
                                [1.56, 50.0],
                                [0.0, 49.01],
                                [-1.56, 50.0],
                                [-215.29, 49.84],
                                [-226.19, 47.89],
                                [-237.13, 43.39],
                                [-246.33, 36.97],
                                [-256.07, 27.61],
                                [-262.38, 19.81],
                                [-264.03, 16.69],
                                [-264.99, 12.01],
                                [-263.22, 5.77],
                                [-260.37, 2.65],
                                [-257.71, 1.09],
                            ],
                        ],
                    },
                    {
                        roiName: 'CouchInterior',
                        holeIn: 'CouchShell',
                        density: { relElectron: 0.001, relMass: 0.001 },
                        loopsXyMm: [
                            [
                                [-252.675, 4.19],
                                [254.345, 4.31],
                                [259.025, 7.42],
                                [260.425, 10.45],
                                [260.415, 13.57],
                                [258.425, 18.25],
                                [250.315, 27.61],
                                [241.795, 35.41],
                                [234.065, 40.36],
                                [224.705, 44.08],
                                [215.345, 45.81],
                                [-215.235, 45.81],
                                [-224.595, 44.08],
                                [-235.515, 39.54],
                                [-243.315, 34.13],
                                [-253.085, 24.49],
                                [-259.275, 16.69],
                                [-260.425, 12.01],
                                [-258.915, 7.42],
                                [-255.795, 4.94],
                                [-252.905, 4.21],
                            ],
                        ],
                    },
                ],
            },
            {
                id: 'medium',
                labelId: 'couch.variant.medium',
                components: [
                    {
                        roiName: 'CouchShell',
                        density: { relElectron: 0.702, relMass: 0.702 },
                        loopsXyMm: [
                            [
                                [-257.41, 0.93],
                                [-251.17, 0.0],
                                [252.73, 0.01],
                                [258.97, 1.67],
                                [263.19, 5.77],
                                [264.93, 12.01],
                                [263.98, 16.69],
                                [261.31, 21.37],
                                [251.66, 32.29],
                                [242.92, 40.09],
                                [233.82, 46.33],
                                [221.53, 52.06],
                                [209.05, 55.53],
                                [182.24, 60.37],
                                [159.13, 62.33],
                                [7.8, 62.5],
                                [0.0, 61.59],
                                [-7.8, 62.5],
                                [-148.21, 62.5],
                                [-171.61, 61.54],
                                [-193.45, 58.65],
                                [-214.84, 54.13],
                                [-227.77, 49.5],
                                [-238.69, 43.23],
                                [-253.18, 30.73],
                                [-261.31, 21.37],
                                [-263.98, 16.69],
                                [-264.93, 12.01],
                                [-263.95, 7.33],
                                [-261.98, 4.21],
                                [-257.69, 1.09],
                            ],
                        ],
                    },
                    {
                        roiName: 'CouchInterior',
                        holeIn: 'CouchShell',
                        density: { relElectron: 0.001, relMass: 0.001 },
                        loopsXyMm: [
                            [
                                [252.73, 4.0],
                                [257.24, 5.67],
                                [259.84, 8.79],
                                [260.48, 11.91],
                                [259.27, 16.59],
                                [249.61, 28.2],
                                [237.13, 39.08],
                                [227.78, 44.67],
                                [216.85, 49.05],
                                [198.13, 53.44],
                                [179.41, 56.49],
                                [149.77, 58.4],
                                [-149.77, 58.4],
                                [-179.41, 56.49],
                                [-198.13, 53.44],
                                [-216.85, 49.05],
                                [-227.78, 44.67],
                                [-237.13, 39.08],
                                [-249.61, 28.2],
                                [-259.27, 16.59],
                                [-260.48, 11.91],
                                [-259.84, 8.79],
                                [-257.24, 5.67],
                                [-253.55, 4.11],
                            ],
                        ],
                    },
                ],
            },
            {
                id: 'thick',
                labelId: 'couch.variant.thick',
                components: [
                    {
                        roiName: 'CouchShell',
                        density: { relElectron: 0.702, relMass: 0.702 },
                        loopsXyMm: [
                            [
                                [-257.21, 0.94],
                                [-252.53, 0.0],
                                [254.49, 0.17],
                                [260.57, 2.65],
                                [263.42, 5.77],
                                [264.79, 8.89],
                                [264.72, 15.13],
                                [261.52, 21.37],
                                [251.9, 32.29],
                                [239.95, 43.21],
                                [226.41, 52.98],
                                [213.25, 60.37],
                                [198.33, 66.71],
                                [184.29, 70.92],
                                [170.25, 73.63],
                                [156.21, 74.88],
                                [1.76, 75.0],
                                [0.2, 74.01],
                                [-1.36, 75.0],
                                [-149.57, 75.0],
                                [-169.85, 73.63],
                                [-188.44, 69.73],
                                [-207.29, 62.97],
                                [-224.17, 54.13],
                                [-241.45, 41.65],
                                [-255.87, 27.61],
                                [-261.12, 21.37],
                                [-263.83, 16.69],
                                [-264.79, 12.01],
                                [-264.39, 8.89],
                                [-261.89, 4.3],
                                [-257.51, 1.09],
                            ],
                        ],
                    },
                    {
                        roiName: 'CouchInterior',
                        holeIn: 'CouchShell',
                        density: { relElectron: 0.001, relMass: 0.001 },
                        loopsXyMm: [
                            [
                                [-252.73, 4.0],
                                [252.73, 4.0],
                                [257.25, 5.77],
                                [259.91, 8.89],
                                [260.36, 13.57],
                                [259.24, 16.69],
                                [250.26, 27.61],
                                [238.69, 38.55],
                                [223.09, 49.9],
                                [209.05, 57.58],
                                [191.89, 64.38],
                                [174.73, 68.69],
                                [163.81, 70.26],
                                [149.77, 71.0],
                                [-149.77, 71.0],
                                [-170.05, 69.47],
                                [-187.21, 65.79],
                                [-205.93, 59.04],
                                [-221.28, 51.01],
                                [-234.86, 41.65],
                                [-248.77, 29.17],
                                [-259.24, 16.69],
                                [-260.36, 13.57],
                                [-259.91, 8.89],
                                [-257.25, 5.77],
                                [-253.86, 4.21],
                            ],
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: 'varian_halcyon',
        name: 'Halcyon Couch',
        variants: [
            {
                id: 'default',
                components: [
                    {
                        roiName: 'CouchShell',
                        density: { relElectron: 0.702, relMass: 0.702 },
                        loopsXyMm: [
                            [
                                [264.95, 11.3],
                                [264.95, 13.25],
                                [263.94, 17.15],
                                [261.66, 21.06],
                                [253.24, 30.83],
                                [241.82, 41.11],
                                [235.96, 45.2],
                                [228.15, 49.54],
                                [224.24, 51.28],
                                [212.53, 54.97],
                                [194.95, 58.57],
                                [183.23, 60.41],
                                [167.6, 62.01],
                                [150.03, 62.63],
                                [9.4, 62.63],
                                [1.59, 62.01],
                                [-2.32, 62.01],
                                [-10.13, 62.63],
                                [-150.76, 62.63],
                                [-170.29, 61.77],
                                [-180.05, 60.76],
                                [-193.72, 58.72],
                                [-213.26, 54.75],
                                [-215.21, 53.98],
                                [-219.11, 53.12],
                                [-224.97, 50.88],
                                [-232.79, 47.08],
                                [-238.65, 43.32],
                                [-244.51, 38.93],
                                [-255.02, 28.87],
                                [-262.9, 19.11],
                                [-264.63, 15.2],
                                [-264.95, 13.25],
                                [-264.81, 9.34],
                                [-263.0, 5.44],
                                [-261.24, 3.48],
                                [-258.18, 1.22],
                                [-254.27, 0.13],
                                [-252.32, 0.0],
                                [251.59, 0.0],
                                [257.45, 0.82],
                                [261.29, 3.48],
                                [264.12, 7.39],
                            ],
                        ],
                    },
                    {
                        roiName: 'CouchInterior',
                        holeIn: 'CouchShell',
                        density: { relElectron: 0.001, relMass: 0.001 },
                        loopsXyMm: [
                            [
                                [260.53, 13.25],
                                [260.12, 15.2],
                                [257.84, 19.11],
                                [252.88, 24.97],
                                [247.22, 30.83],
                                [239.96, 37.32],
                                [234.1, 41.45],
                                [228.24, 44.68],
                                [222.38, 47.48],
                                [220.43, 47.92],
                                [218.47, 48.81],
                                [202.85, 52.8],
                                [195.04, 54.09],
                                [193.08, 54.75],
                                [173.55, 57.38],
                                [157.93, 58.48],
                                [152.07, 58.6],
                                [-152.62, 58.6],
                                [-168.24, 57.87],
                                [-178.01, 56.85],
                                [-193.63, 54.55],
                                [-195.59, 53.88],
                                [-203.4, 52.6],
                                [-205.35, 51.93],
                                [-211.21, 50.85],
                                [-222.93, 47.16],
                                [-230.74, 43.36],
                                [-234.65, 40.9],
                                [-242.46, 35.04],
                                [-247.04, 30.83],
                                [-256.13, 20.99],
                                [-259.06, 17.15],
                                [-260.42, 13.25],
                                [-260.53, 11.3],
                                [-258.88, 7.39],
                                [-256.13, 5.15],
                                [-254.18, 4.4],
                                [-252.23, 4.11],
                                [-248.32, 4.0],
                                [251.68, 4.03],
                                [255.58, 4.73],
                                [258.94, 7.39],
                                [260.3, 9.34],
                            ],
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: 'ibeam_evo',
        name: 'iBeam evo Couch',
        variants: [
            {
                id: 'standard',
                labelId: 'couch.variant.standard',
                components: [
                    {
                        roiName: 'CouchShell',
                        density: { relElectron: 0.702, relMass: 0.702 },
                        loopsXyMm: [
                            [
                                [-264.6, 2.4],
                                [-264.0, 0.8],
                                [-260.9, 0.0],
                                [260.9, 0.0],
                                [264.0, 0.8],
                                [264.6, 2.4],
                                [264.0, 4.0],
                                [262.8, 4.6],
                                [242.9, 5.5],
                                [238.6, 7.3],
                                [232.9, 15.4],
                                [216.4, 45.1],
                                [212.2, 48.8],
                                [209.8, 50.1],
                                [207.0, 50.4],
                                [-207.0, 50.4],
                                [-209.8, 50.1],
                                [-212.2, 48.8],
                                [-216.4, 45.1],
                                [-232.9, 15.4],
                                [-238.6, 7.3],
                                [-242.9, 5.5],
                                [-256.4, 5.3],
                                [-262.8, 4.6],
                                [-264.4, 3.5],
                            ],
                        ],
                    },
                    {
                        roiName: 'CouchInterior',
                        holeIn: 'CouchShell',
                        density: { relElectron: 0.001, relMass: 0.001 },
                        loopsXyMm: [
                            [
                                [-235.0, 4.8],
                                [-235.0, 3.8],
                                [-232.0, 3.6],
                                [-215.8, 3.7],
                                [215.8, 3.7],
                                [232.0, 3.6],
                                [235.0, 3.8],
                                [235.0, 4.8],
                                [213.3, 43.8],
                                [212.2, 45.0],
                                [210.8, 46.0],
                                [209.7, 46.7],
                                [208.4, 47.1],
                                [207.0, 47.4],
                                [-207.0, 47.4],
                                [-208.4, 47.1],
                                [-209.7, 46.7],
                                [-210.8, 46.0],
                                [-212.2, 45.0],
                                [-213.3, 43.8],
                            ],
                        ],
                    },
                ],
            },
            {
                id: 'hn_extension',
                labelId: 'couch.variant.hnExtension',
                components: [
                    {
                        roiName: 'CouchShell',
                        density: { relElectron: 0.702, relMass: 0.702 },
                        loopsXyMm: [
                            [
                                [-141.2, 2.1],
                                [-140.2, 0.6],
                                [-138.8, 0.0],
                                [138.8, 0.0],
                                [140.2, 0.6],
                                [141.2, 2.1],
                                [140.2, 3.9],
                                [137.8, 4.7],
                                [133.2, 5.3],
                                [120.2, 5.3],
                                [116.8, 6.2],
                                [107.7, 17.6],
                                [105.1, 19.5],
                                [103.4, 19.9],
                                [-103.4, 19.9],
                                [-105.1, 19.5],
                                [-107.7, 17.6],
                                [-116.8, 6.2],
                                [-120.2, 5.3],
                                [-133.2, 5.3],
                                [-137.8, 4.7],
                                [-140.2, 3.9],
                                [-141.1, 2.6],
                            ],
                        ],
                    },
                    {
                        roiName: 'CouchInterior',
                        holeIn: 'CouchShell',
                        density: { relElectron: 0.001, relMass: 0.001 },
                        loopsXyMm: [
                            [
                                [-111.1, 4.8],
                                [-110.9, 3.8],
                                [-107.3, 3.6],
                                [-99.6, 3.5],
                                [99.6, 3.5],
                                [107.3, 3.6],
                                [110.9, 3.8],
                                [111.1, 4.8],
                                [106.6, 13.5],
                                [105.5, 14.7],
                                [104.1, 15.7],
                                [103.0, 16.4],
                                [101.7, 16.8],
                                [100.3, 17.1],
                                [-100.3, 17.1],
                                [-101.7, 16.8],
                                [-103.0, 16.4],
                                [-104.1, 15.7],
                                [-105.5, 14.7],
                                [-106.6, 13.5],
                            ],
                        ],
                    },
                ],
            },
        ],
    },
    /**
     * Radixact. The only entry here built from MEASURED data rather than a
     * vendor table: Accuray ships no couch contour at all and renders the couch
     * into its "Couch-replaced CT Series" instead, identically on every slice.
     * These loops are that render, traced at the iso-level half-way between air
     * and each shell's peak. The trace was cross-checked against clinical
     * cases outside this repository; no test here re-verifies that fit.
     *
     * TWO nested bodies, not one. `CouchShell` is the couch top, as on every
     * other model here; the pallet the patient lies on is a SEPARATE body 23 mm
     * above it and is the one that meets y = 0. The split matters for dose --
     * the couch top carries 9.3 mm of the couch's 14.2 mm water-equivalent
     * path, and a single slab at the pallet would put all of it 40 mm too
     * far anterior.
     *
     * The densities are fitted to THESE polygons, not measured material
     * properties: each value is whatever reproduces the rendered areal density
     * through the loops as written, with each interior's density in force
     * inside it — which is what cutting the interiors out of their shells
     * writes. Re-simplifying a loop invalidates the density that belongs to
     * it.
     */
    {
        id: 'accuray_radixact',
        name: 'Radixact Couch',
        variants: [
            {
                id: 'default',
                components: [
                    {
                        roiName: 'CouchPalletShell',
                        density: { relElectron: 0.442, relMass: 0.442 },
                        loopsXyMm: [
                            [
                                [261.92, 22.55],
                                [-261.59, 22.5],
                                [-263.63, 20.51],
                                [-264.33, 18.56],
                                [-264.24, 3.88],
                                [-263.53, 0.94],
                                [-261.59, 0],
                                [-245.93, 0.05],
                                [-244.96, 0.53],
                                [-241.04, 0.55],
                                [251.15, 0.55],
                                [254.09, 0.04],
                                [261.92, 0.06],
                                [263.41, 0.94],
                                [264.08, 2.9],
                                [264.39, 16.6],
                                [264.04, 19.54],
                            ],
                        ],
                    },
                    {
                        roiName: 'CouchPalletInterior',
                        holeIn: 'CouchPalletShell',
                        density: { relElectron: 0.15, relMass: 0.15 },
                        loopsXyMm: [
                            [
                                [-259.63, 19.62],
                                [-260.65, 18.56],
                                [-260.83, 5.84],
                                [-260.61, 3.41],
                                [-253.76, 2.96],
                                [260.94, 2.88],
                                [260.94, 15.36],
                                [260.56, 18.56],
                                [259.82, 19.54],
                            ],
                        ],
                    },
                    {
                        roiName: 'CouchShell',
                        density: { relElectron: 0.873, relMass: 0.873 },
                        loopsXyMm: [
                            [
                                [191.46, 63.65],
                                [-190.16, 63.92],
                                [-193.09, 62.86],
                                [-194.3, 61.61],
                                [-195, 59.66],
                                [-195.05, 53.52],
                                [-196.03, 51.36],
                                [-216.58, 31.56],
                                [-217.84, 29.32],
                                [-218.03, 27.36],
                                [-217.56, 26.04],
                                [-215.6, 23.78],
                                [-212.66, 23.22],
                                [-165.7, 23.25],
                                [-148.08, 27.18],
                                [-143.19, 27.41],
                                [-139.28, 25.82],
                                [-132.43, 25.59],
                                [-130.47, 24.83],
                                [-127.53, 24.6],
                                [-124.6, 24.65],
                                [-122.64, 25.66],
                                [-116.77, 25.8],
                                [-113.83, 27.28],
                                [-111.88, 27.41],
                                [-93.29, 23.23],
                                [90.68, 23.22],
                                [96.55, 25.23],
                                [107.31, 27.31],
                                [111.22, 27.3],
                                [114.16, 25.8],
                                [133.73, 25.74],
                                [136.67, 25.8],
                                [140.58, 27.43],
                                [143.52, 27.29],
                                [161.13, 23.25],
                                [214.24, 23.45],
                                [218.03, 27.36],
                                [216.9, 30.93],
                                [196.36, 51.05],
                                [194.88, 53.78],
                                [194.65, 60.63],
                            ],
                        ],
                    },
                    {
                        roiName: 'CouchInterior',
                        holeIn: 'CouchShell',
                        density: { relElectron: 0.104, relMass: 0.104 },
                        loopsXyMm: [
                            [
                                [190.48, 61.49],
                                [189.51, 61.51],
                                [-190.16, 61.52],
                                [-191.14, 60.89],
                                [-191.69, 59.66],
                                [-192.43, 50.85],
                                [-195.61, 46.93],
                                [-214.56, 28.34],
                                [-214.33, 27.36],
                                [-212.66, 26.69],
                                [-165.7, 26.32],
                                [-147.1, 29.53],
                                [-130.47, 29.73],
                                [-106.98, 29.22],
                                [-97.2, 26.95],
                                [-92.31, 26.56],
                                [91.65, 26.45],
                                [108.29, 29.68],
                                [120.03, 29.99],
                                [146.45, 29.57],
                                [164.06, 26.55],
                                [199.29, 26.4],
                                [212.01, 26.58],
                                [213.97, 27.03],
                                [214.42, 28.34],
                                [213.97, 29.05],
                                [193.42, 49.39],
                                [192.09, 52.81],
                                [191.5, 60.63],
                            ],
                        ],
                    },
                    {
                        roiName: 'CouchRails',
                        // Inside the couch top's wall, in a notch of its interior.
                        holeIn: 'CouchShell',
                        density: { relElectron: 1.377, relMass: 1.377 },
                        loopsXyMm: [
                            [
                                [-107.96, 28.35],
                                [-130.47, 28.65],
                                [-145.96, 28.34],
                                [-141.23, 28.17],
                                [-140.25, 27.15],
                                [-139.28, 26.81],
                                [-132.43, 26.63],
                                [-130.47, 26.04],
                                [-126.56, 25.89],
                                [-124.6, 25.95],
                                [-123.62, 26.5],
                                [-122.64, 26.67],
                                [-116.77, 26.69],
                                [-115.22, 27.36],
                                [-114.81, 28.03],
                                [-113.83, 28.2],
                            ],
                            [
                                [143.51, 28.35],
                                [121.99, 28.71],
                                [106.59, 28.34],
                                [112.2, 28.03],
                                [113.18, 26.94],
                                [116.12, 26.65],
                                [136.67, 26.72],
                                [137.64, 27.13],
                                [138.62, 28.1],
                                [144.25, 28.34],
                            ],
                        ],
                    },
                ],
            },
        ],
    },
] as const satisfies readonly CouchModel[];

/**
 * The library's model ids as a literal union, derived from the entries
 * themselves — a hand-typed id that matches no entry is a COMPILE error
 * wherever this type is required (panel state, apply params, provenance),
 * with {@link requireCouchModel}'s runtime throw as the last line of
 * defence at the one boundary (the model select) where the id arrives as
 * a bare DOM string.
 */
export type CouchModelId = (typeof COUCH_MODELS)[number]['id'];

/**
 * Looks up a couch model by id, throwing rather than returning undefined,
 * and narrowing a bare string to a library entry — the seam the model
 * select resolves its DOM value through.
 */
export function requireCouchModel(
    modelId: string
): (typeof COUCH_MODELS)[number] {
    const model = COUCH_MODELS.find((m) => m.id === modelId);
    if (!model) {
        throw new Error(`Unknown couch model: ${modelId}`);
    }
    return model;
}

/**
 * Looks up a couch model and variant by id, throwing rather than returning
 * undefined: callers use the result unconditionally and a silently missing
 * couch would show the wrong (or no) support structure in a dose-affecting
 * DICOM export.
 */
export function requireCouchVariant(
    modelId: CouchModelId,
    variantId: string
): { model: CouchModel; variant: CouchVariant } {
    // Deliberately returns the INTERFACE types, not the narrowed literal
    // entries: the literal union drops optional fields absent from some
    // entries (e.g. `labelId`), which would make legitimate reads a type
    // error at every call site.
    // The model lookup (and its error message) lives ONCE, in
    // requireCouchModel — statically unreachable here (`modelId` is
    // already a library-derived literal), kept as the delegate's runtime
    // last line of defence.
    const model = requireCouchModel(modelId);
    const variant = model.variants.find((v) => v.id === variantId);
    if (!variant) {
        throw new Error(
            `Unknown variant ${variantId} for couch model ${modelId}`
        );
    }
    return { model, variant };
}
